import { Package, Calendar, Store, Truck, Smartphone, MapPin, Clock, Star, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import FeaturedCarousel from "@/components/FeaturedCarousel";
import ArteknosLogo from "@/components/ArteknosLogo";

export default function LandingPage({ user, onLogout }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0F172A] text-white radial-glow">
      <nav className="sticky top-0 z-40 bg-[#0F172A]/80 backdrop-blur-xl border-b border-white/5 px-4 md:px-8 lg:px-12 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <ArteknosLogo size="sm" showText textSize="text-lg md:text-xl" />
          </div>
          <div className="flex gap-3">
            {user ? (
              <>
                <Button
                  onClick={() => {
                    if (user.role === 'courier') navigate('/courier');
                    else if (user.role === 'partner') navigate('/partner');
                    else if (user.role === 'vendor') navigate('/vendor');
                    else navigate('/home');
                  }}
                  className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full px-6 font-black uppercase tracking-widest text-xs"
                  data-testid="nav-dashboard-btn"
                >
                  Tableau de bord
                </Button>
                <Button
                  onClick={onLogout}
                  variant="outline"
                  className="rounded-full px-6 bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-[#D4AF37] hover:border-[#D4AF37] font-bold uppercase tracking-widest text-xs"
                  data-testid="nav-logout-btn"
                >
                  Déconnexion
                </Button>
              </>
            ) : (
              <Button
                onClick={() => navigate('/auth')}
                className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full px-8 py-5 font-black uppercase tracking-widest text-xs shadow-xl shadow-[#D4AF37]/20 transition-all active:scale-95"
                data-testid="nav-login-btn"
              >
                Connexion
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Carrousel - Vitrines partenaires */}
      <section className="px-4 md:px-8 lg:px-12 py-6">
        <div className="max-w-7xl mx-auto">
          <FeaturedCarousel autoPlayInterval={5000} />
        </div>
      </section>

      {/* Hero Section */}
      <section className="px-4 md:px-8 lg:px-12 py-12 md:py-20 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#003594]/20 blur-3xl pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-2 rounded-full mb-5 text-xs font-bold uppercase tracking-widest" data-testid="hero-badge">
              <MapPin className="w-4 h-4" />
              <span>Dakar, Sénégal — Livraison 7j/7</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight mb-6 leading-[1.05] text-white" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="hero-title">
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5D675] bg-clip-text text-transparent">WÔMA</span> de <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5D675] bg-clip-text text-transparent">ARTEKNOS</span>
            </h1>
            <p className="text-base leading-relaxed text-gray-300 mb-8" data-testid="hero-description">
              Livraison, Taxi, Boutiques & Artisans — pour tout Dakar. Commandez en ligne, payez comme vous voulez, suivez en temps réel.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => navigate(user ? '/orders/new' : '/auth')}
                className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full px-8 py-6 font-black uppercase tracking-widest text-xs shadow-xl shadow-[#D4AF37]/30 transition-all active:scale-95"
                data-testid="hero-cta-button"
              >
                Créer une commande
              </Button>
              <Button
                onClick={() => navigate('/install')}
                variant="outline"
                className="rounded-full px-6 py-6 bg-transparent border-[#003594]/50 text-[#5B8DEF] hover:bg-[#003594] hover:text-white hover:border-[#003594] font-bold uppercase tracking-widest text-xs"
                data-testid="install-app-btn"
              >
                <Smartphone className="w-5 h-5 mr-2" />
                App Mobile
              </Button>
            </div>

            {/* Stats strip */}
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md" data-testid="hero-stats">
              <div className="animate-fade-in-up stagger-1">
                <div className="flex items-center gap-1.5 text-2xl font-black text-[#D4AF37]" style={{fontFamily: 'Outfit, sans-serif'}}>
                  <Clock className="w-5 h-5" />
                  <span>&lt;30min</span>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest font-bold">Délai moyen</p>
              </div>
              <div className="animate-fade-in-up stagger-2">
                <div className="flex items-center gap-1.5 text-2xl font-black text-[#D4AF37]" style={{fontFamily: 'Outfit, sans-serif'}}>
                  <Star className="w-5 h-5 fill-current" />
                  <span>4.8/5</span>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest font-bold">Note moyenne</p>
              </div>
              <div className="animate-fade-in-up stagger-3">
                <div className="flex items-center gap-1.5 text-2xl font-black text-[#D4AF37]" style={{fontFamily: 'Outfit, sans-serif'}}>
                  <Sparkles className="w-5 h-5" />
                  <span>GPS</span>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest font-bold">Suivi temps réel</p>
              </div>
            </div>
          </div>
          <div className="relative animate-fade-in-up stagger-2 flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/30 via-transparent to-[#003594]/40 rounded-full blur-3xl" aria-hidden="true" />
            <img
              src="/woma-logo.png"
              alt="WÔMA de ARTEKNOS"
              className="relative w-72 md:w-[420px] lg:w-[500px] aspect-square object-cover rounded-full ring-4 ring-[#D4AF37]/60 shadow-[0_0_80px_rgba(212,175,55,0.4)]"
              data-testid="hero-image"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="px-4 md:px-8 lg:px-12 py-16 bg-[#1E293B]/40">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight mb-12 text-center text-white" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="services-title">
            Nos <span className="text-[#D4AF37]">Services</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-6 bg-[#1E293B] border border-white/10 shadow-xl rounded-2xl p-6 hover-lift animate-fade-in-up stagger-1 hover:border-[#D4AF37]/50 transition-all" data-testid="service-card-delivery">
              <Truck className="w-12 h-12 text-[#D4AF37] mb-4" />
              <h3 className="text-2xl font-black uppercase tracking-tight mb-2 text-white" style={{fontFamily: 'Outfit, sans-serif'}}>Livraison Express</h3>
              <p className="text-sm leading-relaxed text-gray-400">
                Livraison rapide pour particuliers et entreprises. Suivi en temps réel de vos colis.
              </p>
            </div>
            <div className="md:col-span-6 bg-[#1E293B] border border-white/10 shadow-xl rounded-2xl p-6 hover-lift animate-fade-in-up stagger-2 hover:border-[#D4AF37]/50 transition-all" data-testid="service-card-commerce">
              <Store className="w-12 h-12 text-[#D4AF37] mb-4" />
              <h3 className="text-2xl font-black uppercase tracking-tight mb-2 text-white" style={{fontFamily: 'Outfit, sans-serif'}}>Commerce & Boutiques</h3>
              <p className="text-sm leading-relaxed text-gray-400">
                Gestion complète de catalogue et livraison pour votre commerce en ligne.
              </p>
            </div>
            <div className="md:col-span-6 bg-[#1E293B] border border-white/10 shadow-xl rounded-2xl p-6 hover-lift animate-fade-in-up stagger-3 hover:border-[#D4AF37]/50 transition-all" data-testid="service-card-tailor">
              <Package className="w-12 h-12 text-[#D4AF37] mb-4" />
              <h3 className="text-2xl font-black uppercase tracking-tight mb-2 text-white" style={{fontFamily: 'Outfit, sans-serif'}}>ARTISANS</h3>
              <p className="text-sm leading-relaxed text-gray-400">
                Agenda intégré pour gérer vos rendez-vous clients et livraisons.
              </p>
            </div>
            <div className="md:col-span-6 bg-[#1E293B] border border-white/10 shadow-xl rounded-2xl p-6 hover-lift animate-fade-in-up stagger-4 hover:border-[#D4AF37]/50 transition-all" data-testid="service-card-payment">
              <Calendar className="w-12 h-12 text-[#D4AF37] mb-4" />
              <h3 className="text-2xl font-black uppercase tracking-tight mb-2 text-white" style={{fontFamily: 'Outfit, sans-serif'}}>Paiement Flexible</h3>
              <p className="text-sm leading-relaxed text-gray-400">
                Paiement en ligne sécurisé ou à la livraison. Rappels SMS et WhatsApp.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="px-4 md:px-8 lg:px-12 py-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight mb-12 text-center text-white" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="how-it-works-title">
            Comment ça <span className="text-[#D4AF37]">marche</span> ?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center animate-fade-in-up stagger-1" data-testid="step-1">
              <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] text-[#0F172A] rounded-full flex items-center justify-center text-2xl font-black mx-auto mb-4 shadow-xl shadow-[#D4AF37]/30">
                1
              </div>
              <h3 className="text-lg font-black uppercase tracking-tight mb-2 text-white" style={{fontFamily: 'Outfit, sans-serif'}}>Créez votre commande</h3>
              <p className="text-sm text-gray-400">
                Renseignez les détails de votre livraison ou prenez rendez-vous.
              </p>
            </div>
            <div className="text-center animate-fade-in-up stagger-2" data-testid="step-2">
              <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] text-[#0F172A] rounded-full flex items-center justify-center text-2xl font-black mx-auto mb-4 shadow-xl shadow-[#D4AF37]/30">
                2
              </div>
              <h3 className="text-lg font-black uppercase tracking-tight mb-2 text-white" style={{fontFamily: 'Outfit, sans-serif'}}>Choisissez le paiement</h3>
              <p className="text-sm text-gray-400">
                Payez en ligne ou à la livraison selon votre préférence.
              </p>
            </div>
            <div className="text-center animate-fade-in-up stagger-3" data-testid="step-3">
              <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] text-[#0F172A] rounded-full flex items-center justify-center text-2xl font-black mx-auto mb-4 shadow-xl shadow-[#D4AF37]/30">
                3
              </div>
              <h3 className="text-lg font-black uppercase tracking-tight mb-2 text-white" style={{fontFamily: 'Outfit, sans-serif'}}>Recevez et suivez</h3>
              <p className="text-sm text-gray-400">
                Notifications SMS/WhatsApp et suivi en temps réel.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="relative overflow-hidden px-4 md:px-8 lg:px-12 py-20 bg-gradient-to-br from-[#003594] via-[#003380] to-[#0F172A] text-white grain">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#D4AF37]/30 rounded-full blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-[#003594]/40 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tight mb-6 text-white" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="footer-cta-title">
            Prêt à <span className="text-[#D4AF37]">expédier</span> ?
          </h2>
          <p className="text-base text-white/80 mb-8 max-w-2xl mx-auto">
            Rejoignez des milliers d'utilisateurs satisfaits qui font confiance à WÔMA de ARTEKNOS.
          </p>
          <Button
            onClick={() => navigate('/auth')}
            className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full px-10 py-6 font-black uppercase tracking-widest text-xs shadow-2xl shadow-[#D4AF37]/30 transition-all active:scale-95"
            data-testid="footer-cta-button"
          >
            Commencer maintenant
          </Button>
        </div>
      </section>

      {/* Devenez Partenaire Section */}
      <section className="px-4 md:px-8 lg:px-12 py-16 bg-[#1E293B]/40">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight mb-4 text-white" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="partner-section-title">
            Rejoignez <span className="text-[#D4AF37]">WÔMA</span>
          </h2>
          <p className="text-base text-gray-400 mb-8">
            Que vous soyez commerçant, artisan, entrepreneur ou coursier, développez votre activité avec notre plateforme de livraison.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Devenir Partenaire */}
            <div className="bg-[#1E293B] border-2 border-[#003594]/40 rounded-2xl p-6 hover:border-[#003594] transition-all">
              <h3 className="text-lg font-black uppercase tracking-tight mb-3 text-white" style={{fontFamily: 'Outfit, sans-serif'}}>Devenez Partenaire</h3>
              <p className="text-xs text-gray-400 mb-6 uppercase tracking-wider">
                Commerçants, artisans, boutiques : gérez vos livraisons facilement
              </p>
              <a
                href="https://wa.me/221769097780?text=Bonjour,%20je%20souhaite%20devenir%20partenaire%20ARTEKNOS%20Livraison%20Express"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 w-full bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full px-6 py-4 font-bold uppercase tracking-widest text-xs shadow-lg hover:shadow-xl transition-all active:scale-95"
                data-testid="whatsapp-partner-btn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Contactez-nous
              </a>
              <p className="text-[10px] text-gray-500 mt-3 uppercase tracking-widest font-bold">+221 76 909 77 80</p>
            </div>

            {/* Devenir Coursier */}
            <div className="bg-[#1E293B] border-2 border-[#D4AF37]/40 rounded-2xl p-6 hover:border-[#D4AF37] transition-all">
              <h3 className="text-lg font-black uppercase tracking-tight mb-3 text-white" style={{fontFamily: 'Outfit, sans-serif'}}>Devenez Coursier</h3>
              <p className="text-xs text-gray-400 mb-6 uppercase tracking-wider">
                Formez-vous et rejoignez notre équipe de livreurs professionnels
              </p>
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLScIC34Pux85ZF1_OamUnxhxr-4duImQsU5BIST2loJdcp7eXw/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full px-6 py-4 font-black uppercase tracking-widest text-xs shadow-lg hover:shadow-xl transition-all active:scale-95"
                data-testid="apply-courier-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Postuler et se former
              </a>
              <p className="text-[10px] text-gray-500 mt-3 uppercase tracking-widest font-bold">Formation incluse</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] border-t border-white/5 text-gray-400 px-4 md:px-8 lg:px-12 py-8">
        <div className="max-w-7xl mx-auto text-center relative">
          <p className="text-xs uppercase tracking-widest font-bold">
            © 2026 WÔMA de ARTEKNOS · Tous droits réservés
          </p>
          <button
            onClick={() => navigate('/admin-private-126')}
            className="absolute right-0 bottom-0 text-xs opacity-20 hover:opacity-100 transition-opacity"
            data-testid="admin-access-btn"
          >
            •
          </button>
        </div>
      </footer>
    </div>
  );
}
